import React, { useState, useCallback } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { MultiFileDropzone } from '@Components/FileUploadDropzone/MultiFileDropzone';
import { ConfirmModal } from '@Components/ConfirmModal/ConfirmModal';
import styles from './DirectFileUpload.module.scss';
import { DropzoneConfig } from '@Types';
import { PresignedURLTypesEnum } from '@Types/Documents';
import { FileList } from './FileList';
import { useDirectFileUpload } from '@Hooks/useDirectFileUpload';

interface DirectFileUploadProps {
  fieldName: string;
  config: DropzoneConfig;
  presignedUrlType: PresignedURLTypesEnum;
  title?: string;
  subTitle?: string;
  placeholderText?: string;
  required?: boolean;
  additionalFields?: Record<string, any>;
  onUploadStateChange?: (hasActiveUploads: boolean) => void;
}

export const DirectFileUpload = ({
  fieldName,
  config,
  presignedUrlType,
  title,
  subTitle,
  placeholderText,
  required,
  additionalFields,
  onUploadStateChange,
}: DirectFileUploadProps) => {
  const { t } = useTranslation('common');

  const { errors, control } = useFormContext();
  const [fileToDelete, setFileToDelete] = useState<string | number | null>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const maxFiles = config?.maxFiles;

  const {
    activeUploads,
    failedUploads,
    localCompletedFiles,
    inlineError,
    rejectionInfo,
    currentCount,
    handleFilesSelected,
    deleteFile,
    retryUpload,
    getProgressById,
  } = useDirectFileUpload({
    fieldName,
    presignedUrlType,
    additionalFields,
    maxFiles,
    onUploadStateChange,
  });

  const disableDropzone = maxFiles && currentCount >= maxFiles;
  const hasFileError = !!errors[fieldName];

  const handleDeleteFile = useCallback((id: string | number) => {
    setFileToDelete(id);
    setIsConfirmModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (fileToDelete === null) return;
    deleteFile(fileToDelete);
    setFileToDelete(null);
    setIsConfirmModalOpen(false);
  }, [fileToDelete, deleteFile]);

  const handleCancelDelete = useCallback(() => {
    setFileToDelete(null);
    setIsConfirmModalOpen(false);
  }, []);

  return (
    <div
      className={classnames(styles.file, {
        [styles.hasError]: hasFileError || !!inlineError,
      })}
    >
      {title && (
        <div className={styles.titleContainer}>
          <h3 className={styles.title}>
            {title}
            {required && <span className={styles.required}>*</span>}
          </h3>
          {subTitle && <p className={styles.subtitle}>{subTitle}</p>}
        </div>
      )}

      {errors[fieldName] && (
        <div className={styles.errorContainer}>
          {/* Always show the error message. */}
          <div className={styles.errorMessage}>{errors[fieldName]?.message}</div>
          {/* Only show the oversized files error if there is not a too many files error. */}
          {!rejectionInfo?.tooManyFiles &&
            rejectionInfo?.oversizedFiles &&
            rejectionInfo.oversizedFiles.length > 0 && (
              <ul className={styles.errorFileList}>
                {rejectionInfo.oversizedFiles.map((file, index) => (
                  <li key={index} className={styles.errorFileItem}>
                    {file.name} ({(file.size / (1024 * 1024)).toFixed(1)}
                    {t('units.mb')})
                  </li>
                ))}
              </ul>
            )}
        </div>
      )}

      <Controller
        name={fieldName}
        control={control}
        defaultValue={[]}
        render={() => (
          <MultiFileDropzone
            fieldName={fieldName}
            onFilesSelected={handleFilesSelected}
            placeholderText={placeholderText}
            disabled={disableDropzone || activeUploads.length > 0}
            required={required}
            maxFiles={config?.maxFiles}
            maxFileSizeInMB={config?.maxFileSizeInMB}
            currentCount={currentCount}
          />
        )}
      />

      {(localCompletedFiles.length > 0 || activeUploads.length > 0 || failedUploads.length > 0) && (
        <FileList
          pendingUploads={activeUploads}
          failedUploads={failedUploads}
          completedUploads={localCompletedFiles}
          getProgressById={getProgressById}
          onDeleteFile={handleDeleteFile}
          onRetryUpload={retryUpload}
        />
      )}

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={handleCancelDelete}
        title={t('file_upload.remove_file_modal.title')}
        confirmText={t('file_upload.remove_file_modal.confirm')}
        cancelText={t('file_upload.remove_file_modal.cancel')}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isConfirmBtnDanger
        compact
      >
        {t('file_upload.remove_file_modal.text')}
      </ConfirmModal>
    </div>
  );
};
